"use client"

import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import { Components } from 'react-markdown'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  const components: Components = {
    img: ({ node, src, alt, ...props }) => {
      if (!src) return null

      return (
        <span className="block my-6 relative w-full aspect-video">
          <img
            src={src as string | "https://mbg.mn/og.jpg"} 
            alt={alt || ''}
            className="object-contain rounded-lg"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
            {...props}
          />
        </span>
      )
    },

    h1: ({ node, ...props }) => (
      <h1 className="text-4xl font-bold mt-8 mb-4 text-foreground" {...props} />
    ),

    h2: ({ node, ...props }) => (
      <h2 className="text-3xl font-bold mt-6 mb-3 text-foreground" {...props} />
    ),

    h3: ({ node, ...props }) => (
      <h3 className="text-2xl font-semibold mt-5 mb-2 text-foreground" {...props} />
    ),

    h4: ({ node, ...props }) => (
      <h4 className="text-xl font-semibold mt-4 mb-2 text-foreground" {...props} />
    ),

    h5: ({ node, ...props }) => (
      <h5 className="text-lg font-semibold mt-3 mb-2 text-foreground" {...props} />
    ),

    h6: ({ node, ...props }) => (
      <h6 className="text-base font-semibold mt-3 mb-2 text-foreground" {...props} />
    ),

    p: ({ node, children, ...props }) => {
      // Check if paragraph contains only a block-level element (like our custom img span)
      const childArray = React.Children.toArray(children)
      if (childArray.length === 1) {
        const firstChild = childArray[0]
        if (
          React.isValidElement(firstChild) &&
          typeof firstChild.type === 'string' &&
          (firstChild.type === 'span' || firstChild.type === 'div')
        ) {
          return <>{children}</>
        }
      }

      return <p className="mb-4 text-foreground leading-relaxed" {...props}>{children}</p>
    },

    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-foreground" {...props} />
    ),

    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-foreground" {...props} />
    ),

    li: ({ node, ...props }) => (
      <li className="ml-4 text-foreground" {...props} />
    ),

    a: ({ node, ...props }) => (
      <a
        className="text-primary hover:text-primary/80 underline transition-colors"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    ),

    blockquote: ({ node, ...props }) => (
      <blockquote
        className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground"
        {...props}
      />
    ),

    code: ({ node, className, children, ...props }) => {
      const isInline = !className?.includes('language-')
      
      if (isInline) {
        return (
          <code
            className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground"
            {...props}
          >
            {children}
          </code>
        )
      }
      return (
        <code
          className="block bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono text-foreground my-4"
          {...props}
        >
          {children}
        </code>
      )
    },

    pre: ({ node, ...props }) => (
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4" {...props} />
    ),

    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full border-collapse border-2 border-slate-600 dark:border-slate-500" {...props} />
      </div>
    ),

    thead: ({ node, ...props }) => (
      <thead className="bg-muted/50" {...props} />
    ),

    tbody: ({ node, ...props }) => (
      <tbody {...props} />
    ),

    tr: ({ node, ...props }) => (
      <tr className="border-b border-slate-600 dark:border-slate-500 hover:bg-muted/30 transition-colors" {...props} />
    ),

    th: ({ node, ...props }) => (
      <th
        className="border border-slate-600 dark:border-slate-500 px-4 py-2 bg-muted font-semibold text-left text-foreground"
        {...props}
      />
    ),

    td: ({ node, ...props }) => (
      <td className="border border-slate-600 dark:border-slate-500 px-4 py-2 text-foreground" {...props} />
    ),

    hr: ({ node, ...props }) => (
      <hr className="my-8 border-border" {...props} />
    ),

    strong: ({ node, ...props }) => (
      <strong className="font-bold text-foreground" {...props} />
    ),

    em: ({ node, ...props }) => (
      <em className="italic text-foreground" {...props} />
    ),

    del: ({ node, ...props }) => (
      <del className="line-through text-muted-foreground" {...props} />
    ),
  }

  return (
    <div className={`prose max-w-none dark:prose-invert ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}